import { useEffect, useMemo, useState, useRef } from "react";
import ReactQuill from "react-quill";
import axios from "axios";
import "react-quill/dist/quill.snow.css";
import axiosInstance from "../../../../utils/axiosInstance";

import Icon from "../../../../components/AppIcon";
import { useAuth } from "../../../../context/AuthContext";
import {
  FileText,
  User,
  Calendar,
  ShieldCheck,
  AlertCircle,
  PenLine,
  Image as ImageIcon,
} from "lucide-react";
import TinyMCEFullEditor from "../../../../components/editors/TinyMCEFullEditor";

const API = import.meta.env.VITE_API_BASE_URL;

const RadiologyResultModal = ({
  isOpen,
  onClose,
  test,
  patient,
  order,
  onSave,
}) => {
  const { user } = useAuth();

  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [approved, setApproved] = useState(false);
  const [notes, setNotes] = useState("");

  // ✅ signatures (same as pathology)
  const [sigLoading, setSigLoading] = useState(false);
  const [sigOptions, setSigOptions] = useState([]);
  const [sigDefaults, setSigDefaults] = useState({
    left: null,
    center: null,
    right: null,
  });
  const [manualSigs, setManualSigs] = useState({
    leftSignatureId: null,
    centerSignatureId: null,
    rightSignatureId: null,
  });

  // ✅ separate refs (avoid mixing image handlers)
  const findingsQuillRef = useRef(null); // Radiology Findings (upload -> URL)
  const notesQuillRef = useRef(null); // Clinical Notes (base64)

  useEffect(() => {
    if (isOpen) {
      loadExisting();
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const reset = () => {
    setRemarks("");
    setNotes("");
    setResult(null);
    setApproved(false);
    setSigOptions([]);
    setSigDefaults({ left: null, center: null, right: null });
    setManualSigs({
      leftSignatureId: null,
      centerSignatureId: null,
      rightSignatureId: null,
    });
  };

  const loadExisting = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API}/api/results/find`, {
        params: { orderId: order.id, testId: test.id, patientId: patient.id },
      });

      const data = res.data?.data || null;

      if (data) {
        setResult(data);
        setRemarks(data.reportHtml || "");
        setApproved(data.status === "APPROVED");
        // Parse plain notes (strip __freeTexts wrapper if present)
        try {
          const parsed = JSON.parse(data.notes || "");
          setNotes(parsed.__notes || "");
        } catch {
          setNotes(data.notes || "");
        }
      } else {
        // No saved result — pre-fill findings with FREE_TEXT report item content
        setResult(null);
        setApproved(false);
        setNotes("");
        try {
          const itemsRes = await axiosInstance.get(`${API}/api/report-items/list/${test.id}`);
          const allItems = itemsRes.data?.data || [];
          const ftHtml = allItems
            .filter((it) => it.type === "FREE_TEXT")
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map((it) => it.html || "")
            .filter(Boolean)
            .join("");
          setRemarks(ftHtml); // pre-fill findings editor
        } catch {
          setRemarks("");
        }
      }

      await loadSignatures(data);
    } catch (e) {
      console.error(e);
      reset();
    } finally {
      setLoading(false);
    }
  };

  const loadSignatures = async (existing) => {
    try {
      setSigLoading(true);

      const res = await axios.get(`${API}/api/esignatures/by-test`, {
        params: { testId: test.id },
      });

      const data = res.data?.data || {};
      const defaults = data.defaults || {
        left: null,
        center: null,
        right: null,
      };
      const options = data.signatures || [];

      setSigDefaults(defaults);
      setSigOptions(options);

      setManualSigs({
        leftSignatureId: existing?.leftSignatureId ?? defaults.left ?? null,
        centerSignatureId:
          existing?.centerSignatureId ?? defaults.center ?? null,
        rightSignatureId: existing?.rightSignatureId ?? defaults.right ?? null,
      });
    } catch (err) {
      console.error("signature load error:", err);
      setSigDefaults({ left: null, center: null, right: null });
      setSigOptions([]);
      setManualSigs({
        leftSignatureId: existing?.leftSignatureId ?? null,
        centerSignatureId: existing?.centerSignatureId ?? null,
        rightSignatureId: existing?.rightSignatureId ?? null,
      });
    } finally {
      setSigLoading(false);
    }
  };

  const isLocked = false;

  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  /* ---------------------------------
     ✅ Findings Quill IMAGE UPLOAD (URL)
  ---------------------------------- */
  const uploadImage = async (file) => {
    const form = new FormData();
    form.append("file", file);

    const res = await axios.post(`${API}/api/uploads`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return res.data?.url || res.data?.data?.url || null;
  };

  const handleFindingsImageInsert = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        setLoading(true);

        const url = await uploadImage(file);
        if (!url) throw new Error("Upload failed: no url");

        const editor = findingsQuillRef.current?.getEditor();
        if (!editor) return;

        const range = editor.getSelection(true);
        const index = range ? range.index : editor.getLength();

        editor.insertEmbed(index, "image", url, "user");
        editor.setSelection(index + 1);
      } catch (e) {
        console.error(e);
        alert("Image upload failed");
      } finally {
        setLoading(false);
      }
    };
  };

  // ✅ Findings modules (disable toolbar when locked)
  const findingsModules = useMemo(() => {
    return {
      toolbar: isLocked
        ? false
        : {
            container: [
              [{ header: [1, 2, 3, false] }],
              ["bold", "italic", "underline"],
              [{ list: "ordered" }, { list: "bullet" }],
              [{ align: [] }],
              ["link", "image"],
              ["clean"],
            ],
            handlers: { image: handleFindingsImageInsert },
          },
      clipboard: { matchVisual: false },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked]);

  /* ---------------------------------
     ✅ Clinical Notes IMAGE (base64 like Pathology)
  ---------------------------------- */
  const handleNotesImageInsert = () => {
    try {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");
      input.click();

      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
          const quill = notesQuillRef.current?.getEditor();
          if (!quill) return;

          const range = quill.getSelection(true);
          const idx = range?.index ?? 0;

          quill.insertEmbed(idx, "image", reader.result);
          quill.setSelection(idx + 1);
        };
        reader.readAsDataURL(file);
      };
    } catch (err) {
      console.error("Notes image insert failed:", err);
    }
  };

  // ✅ Notes modules (exact style as pathology)
  const notesModules = useMemo(() => {
    return {
      toolbar: isLocked
        ? false
        : {
            container: [
              [{ header: [1, 2, 3, false] }],
              ["bold", "italic", "underline"],
              [{ list: "ordered" }, { list: "bullet" }],
              [{ align: [] }],
              ["link", "image"],
              ["clean"],
            ],
            handlers: { image: handleNotesImageInsert },
          },
      clipboard: { matchVisual: false },
    };
  }, [isLocked]);

  /* ---------------- Signature Helpers ---------------- */
  const sigById = useMemo(() => {
    const map = new Map();
    sigOptions.forEach((s) => map.set(s.id, s));
    return map;
  }, [sigOptions]);

  const SignatureSelect = ({ label, value, onChange }) => (
    <div className="w-full">
      <div className="text-xs font-semibold text-gray-700 mb-1">{label}</div>
      <select
        disabled={isLocked || sigLoading}
        className="text-sm border border-gray-300 px-3 py-2.5 w-full rounded-lg bg-white disabled:bg-gray-100 disabled:cursor-not-allowed focus:border-blue-600 focus:ring-2 focus:ring-blue-200 transition-colors"
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value ? Number(e.target.value) : null)
        }>
        <option value="">None</option>
        {sigOptions.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} {s.designation ? `- ${s.designation}` : ""} (
            {String(s.alignment || "").toUpperCase()})
          </option>
        ))}
      </select>

      {value && sigById.get(value) && (
        <div className="mt-2 flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white">
          <img
            src={sigById.get(value).signatureImg}
            alt="signature"
            className="h-10 w-auto object-contain bg-gray-50 rounded-md border border-gray-200 px-2"
          />
          <div className="text-xs text-gray-700">
            <div className="font-semibold">{sigById.get(value).name}</div>
            <div className="text-gray-500">
              {sigById.get(value).qualification || ""}{" "}
              {sigById.get(value).designation
                ? `• ${sigById.get(value).designation}`
                : ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const resetToDefaults = () => {
    setManualSigs({
      leftSignatureId: sigDefaults.left ?? null,
      centerSignatureId: sigDefaults.center ?? null,
      rightSignatureId: sigDefaults.right ?? null,
    });
  };

  /* ---------------------------------
     SAVE / UPDATE
  ---------------------------------- */
  const handleSubmit = async () => {
    if (!patient?.id) return alert("Patient information is required");
    if (!remarks.trim()) return alert("Please enter radiology findings");

    try {
      setLoading(true);

      const notesPayload = notes?.trim() || null;

      const payload = {
        testType: "RADIOLOGY",
        testId: test.id,
        patientId: patient.id,
        orderId: order.id,
        reportedById: user.id,
        createdById: user.id,
        reportHtml: remarks,
        notes: notesPayload,
        approve: approved,
        leftSignatureId: manualSigs.leftSignatureId ?? null,
        centerSignatureId: manualSigs.centerSignatureId ?? null,
        rightSignatureId: manualSigs.rightSignatureId ?? null,
      };

      if (result?.id) {
        const r = await axios.put(`${API}/api/results/${result.id}`, payload);
        setResult(r.data?.data || result);
      } else {
        const r = await axios.post(`${API}/api/results`, payload);
        setResult(r.data?.data || null);
      }

      onSave?.();
      onClose();
    } catch (e) {
      console.error("Radiology Save Error:", e);
      alert("Failed to save radiology report");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/70 flex justify-center items-center p-3 z-50">
      <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-indigo-900 text-white flex-shrink-0">
          <div className="flex justify-between items-center p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-xl backdrop-blur-sm">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold">
                  Radiology Report - {test.name}
                </h2>
                <p className="text-sm text-indigo-100 mt-0.5">
                  Create or edit radiology findings (supports images)
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors">
              <Icon name="X" size={18} className="text-white" />
            </button>
          </div>

          <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4" />
                <span className="font-semibold">Patient</span>
              </div>
              <p className="text-base font-semibold">
                {patient?.fullName || "N/A"}
              </p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4" />
                <span className="font-semibold">Test</span>
              </div>
              <p className="text-base font-semibold">{test?.name || "N/A"}</p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4" />
                <span className="font-semibold">Status</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    isLocked
                      ? "bg-emerald-400"
                      : result?.id
                        ? "bg-amber-400"
                        : "bg-blue-400"
                  }`}
                />
                <p className="text-base font-semibold">
                  {isLocked ? "Approved" : result?.id ? "Draft" : "New"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {isLocked && (
          <div className="bg-emerald-50 border border-emerald-200 mx-5 mt-5 p-4 rounded-xl flex-shrink-0">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-sm font-semibold text-emerald-900">
                  Approved Report
                </p>
                <p className="text-sm text-emerald-700 mt-0.5">
                  This report has been finalized. No edits are allowed.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 bg-slate-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-3 border-gray-300 border-t-indigo-600"></div>
              <p className="mt-4 text-sm text-gray-600">Loading report...</p>
            </div>
          ) : (
            <>
              {/* Findings */}
              <div className="mb-6 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-indigo-50 border-b border-indigo-100">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-indigo-700" />
                    <div className="text-sm font-semibold text-indigo-900">
                      Radiology Findings
                    </div>
                  </div>
                  <div className="text-xs text-indigo-700">
                    Use toolbar image button to upload & insert
                  </div>
                </div>

                <div className={`${isLocked ? "bg-gray-50" : "bg-white"} p-3`}>
                  <TinyMCEFullEditor
                    value={remarks || ""}
                    onChange={setRemarks}
                    height={400}
                    placeholder="Write notes..."
                  />
                </div>

                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
                  Tip: include observations, measurements, interpretations.
                  Images will be embedded in the report.
                </div>
              </div>

              {/* ✅ Clinical Notes */}
              <div className="mb-6 bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Clinical Notes
                </label>

                <div
                  className={`rounded-xl border border-gray-300 overflow-hidden bg-white ${
                    isLocked ? "opacity-80" : ""
                  }`}>
                  <TinyMCEFullEditor
                    value={notes || ""}
                    onChange={setNotes}
                    height={200}
                    placeholder="Write notes..."
                  />
                </div>

                <div className="mt-1 text-xs text-gray-500">
                  You can format text (bold, list, headings) and insert images.
                </div>
              </div>

              {/* Signatures */}
              <div className="mb-6 bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <PenLine className="w-4 h-4 text-gray-700" />
                    <div className="text-sm font-semibold text-gray-800">
                      Report Signatures (Manual Override)
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={resetToDefaults}
                    disabled={isLocked || sigLoading}
                    className="text-xs px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed">
                    Reset to Category Default
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 border border-gray-200 rounded-xl p-4">
                  <SignatureSelect
                    label="Left Signature"
                    value={manualSigs.leftSignatureId}
                    onChange={(v) =>
                      setManualSigs((s) => ({ ...s, leftSignatureId: v }))
                    }
                  />
                  <SignatureSelect
                    label="Center Signature"
                    value={manualSigs.centerSignatureId}
                    onChange={(v) =>
                      setManualSigs((s) => ({ ...s, centerSignatureId: v }))
                    }
                  />
                  <SignatureSelect
                    label="Right Signature"
                    value={manualSigs.rightSignatureId}
                    onChange={(v) =>
                      setManualSigs((s) => ({ ...s, rightSignatureId: v }))
                    }
                  />
                </div>

                <div className="mt-2 text-xs text-gray-500">
                  These will be saved inside <b>PatientTestResult</b> for this
                  patient + test only.
                </div>
              </div>

              {/* Approval */}
              {!isLocked && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 mb-2">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        id="approve-report"
                        checked={approved}
                        onChange={(e) => setApproved(e.target.checked)}
                        className="w-4 h-4 text-indigo-600 border-gray-400 rounded focus:ring-indigo-500"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="approve-report"
                          className="text-sm font-semibold text-gray-900 cursor-pointer">
                          Finalize and Approve Report
                        </label>
                        <span className="px-2 py-1 text-xs font-bold bg-indigo-100 text-indigo-800 rounded-md">
                          ADMIN
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        Once approved, the report is final and cannot be edited.
                      </p>
                      {approved && (
                        <div className="mt-4 p-3 bg-indigo-100 border border-indigo-200 rounded-xl">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-indigo-700" />
                            <p className="text-sm font-medium text-indigo-800">
                              The report will be locked upon saving.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-white p-5 flex-shrink-0">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-gray-800">Report by:</span>
                <span className="text-gray-900">
                  {user?.name || user?.username || "N/A"}
                </span>
              </div>
              {result?.updatedAt && (
                <div className="mt-1 text-xs text-gray-500">
                  <span className="font-medium">Last updated:</span>{" "}
                  {formatDate(result.updatedAt)}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="text-sm px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium">
                Cancel
              </button>

              {!isLocked && (
                <button
                  onClick={handleSubmit}
                  disabled={!remarks.trim() || loading}
                  className="text-sm px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 transition-all font-medium shadow-sm hover:shadow">
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <p className="w-4 h-4 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {result?.id ? "Update Report" : "Save Report"}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadiologyResultModal;
